import { isLowesPublicPath } from './lowes-path.ts'

/**
 * Is this URL path part of the public QR landing page (issue #205)?
 *
 * True when the FIRST path segment is "qr" in any letter case: the landing
 * page, /qr/careers and everything under it, their `.data` requests, and any
 * mistyped address under them. Routes match without regard to case, so
 * everything that treats these pages as public (the header, the 404 page,
 * the page title, browser Sentry) asks here, the way the Lowe's form's
 * callers ask app/utils/lowes-path.ts. The two are kept separate so neither
 * form's rules can change the other's.
 *
 * Pure (its one import is pure too): the express server, the app and the
 * browser all import it.
 */
export function isQrPublicPath(pathname: string): boolean {
	const first = firstSegment(decodeEscapes(pathname))
	return first.toLowerCase().replace(/\.data$/, '') === 'qr'
}

/**
 * The wider test, for the rate-limit BUDGET and server Sentry redaction only:
 * does the first path segment START WITH "qr", in any letter case, once
 * control characters are removed? `/qr%00` and `/qr;x` are 404s, not the
 * page, but still count against the page's budget rather than the staff one.
 * No real route's first segment starts with "qr" except these pages.
 */
export function looksLikeQrPath(pathname: string): boolean {
	const path = decodeEscapes(pathname).replace(/[\x00-\x1f\x7f-\x9f]/g, '')
	return firstSegment(path).toLowerCase().startsWith('qr')
}

/**
 * Is this path one of the public, no-login pages: the Lowe's form or /qr?
 * For what the two share (no "Log in", no link to the staff home page, no
 * browser Sentry); what differs between them asks the specific test.
 */
export function isPublicPagePath(pathname: string): boolean {
	return isLowesPublicPath(pathname) || isQrPublicPath(pathname)
}

// The same decoding as lowes-path.ts: escape by escape, so one malformed
// escape later in the address cannot hide an encoded "qr" in front of it.
function decodeEscapes(pathname: string) {
	return pathname.replace(/(?:%[0-9a-f]{2})+/gi, (escapes) => {
		try {
			return decodeURIComponent(escapes)
		} catch {
			return escapes
		}
	})
}

function firstSegment(path: string) {
	return path.split(/[?#]/)[0]!.split('/').find(Boolean) ?? ''
}
